/**
 * Sensitive-data guard for form capture: credentials and card data.
 *
 * `trackSubmit` sends every string field of a submitted form as `data_<name>`,
 * which is the intended default — form values are the analytics payload. Two
 * categories are the exception, each for its own reason: a PAN pulls analytics
 * into PCI-DSS scope, and a password is a credential that must never leave the
 * form. Neither can be un-sent by cleanup downstream.
 *
 * Documentos oficiais (CPF/CNPJ) ficaram FORA por decisão de escopo — são dado
 * pessoal e continuam sendo capturados hoje. Ver o PR que introduziu este bloco
 * antes de assumir que já estão cobertos.
 *
 * Three independent signals, because none of them is sufficient alone:
 *
 * - `autocomplete="cc-*"` — the spec-declared intent. Precise when present, but
 *   plenty of real checkouts never set it.
 * - the field NAME — catches `cardNumber` / `numeroCartao` when autocomplete is
 *   missing. Useless against a field named `field3`, and narrowed by a safe-
 *   qualifier list so it stops eating `cardInstallments` and friends.
 * - the VALUE (Luhn) — catches the innocently-named field, which is exactly the
 *   case the other two miss.
 *
 * Detection redacts by DROPPING the value (same as `type="password"`), never by
 * masking it: a masked PAN is still a PAN in the payload. Callers record which
 * field names were dropped so a redaction that fires where no card should exist
 * is visible instead of silent.
 */

/**
 * Names that are sensitive on their own, whatever else the name says. These are
 * Sensitive Authentication Data under PCI — the one category that must never be
 * stored anywhere, so no qualifier gets to override them.
 */
const STRONG_SENSITIVE_PATTERNS: readonly RegExp[] = [
  /cvv|cvc/i,
  /security[-_\s]?code/i,
  /c(o|ó)digo[-_\s]?(de[-_\s]?)?seguran(c|ç)a/i,
  /\bcc[-_]?(num|number|csc|cvv|cvc|exp)/i,
]

/**
 * Credenciais. Até aqui a senha só era barrada por `type="password"`, e isso
 * furou na prática: apareceram no catálogo campos `Senha` e `Crie uma senha`
 * com o valor em texto puro. Basta o input não ser `password` — um botão
 * "mostrar senha" troca o tipo para `text`, um componente customizado nunca
 * usa o tipo, ou o campo não é alcançável por `form.elements.namedItem()` —
 * para o único sinal existente falhar em silêncio.
 *
 * Diferente de `card`, aqui NÃO existe qualificador seguro: não há nada útil
 * para a análise num campo cujo nome diz "senha". Perder um `senha_valida`
 * booleano é barato; vazar a credencial não tem volta.
 */
const CREDENTIAL_NAME_PATTERNS: readonly RegExp[] = [
  /senha/i,
  /password/i,
  /passwd/i,
  /\bpwd\b/i,
  /\bpin\b/i,
  /\btoken\b/i,
  /\botp\b/i,
  /secret/i,
  /api[-_\s]?key/i,
]

/** Names that put the field in card territory — sensitive unless qualified. */
const CARD_NAME_PATTERNS: readonly RegExp[] = [
  /card/i,
  /cart(a|ã)o/i,
]

/**
 * Qualifiers that make a card-ish name safe to capture. None of these is
 * Cardholder Data: the number of installments, the brand, the card type, a
 * truncated PAN (PCI explicitly allows the last four) or an already-masked value.
 * They are also the fields payment analytics actually wants.
 *
 * Overriding by NAME is only safe because the Luhn check still runs on the VALUE:
 * a field called `cardLastDigits` that somehow holds a full PAN is redacted by
 * the value rule regardless of what its name earned it here.
 */
const SAFE_QUALIFIER_PATTERNS: readonly RegExp[] = [
  /installment|parcel/i,
  /brand|bandeira|flag/i,
  /type|tipo/i,
  /last[-_]?(4|four|digits?)/i,
  /(u|ú)ltimos?[-_]?d(i|í)gitos/i,
  /mask/i,
]

/** A card field only ever holds digits and separators. Anything else — a
 * sentence, an email, JSON — is not a PAN, whatever digits it contains. */
const NUMERIC_ISH = /^[\d\s.-]+$/

/**
 * Precedence: strong names always redact; a card-ish name redacts UNLESS the same
 * name carries a safe qualifier.
 *
 * The narrowing exists because a bare `/card/i` swallowed real analytics:
 * `creditCardInstallments` on the checkout is the number of installments, not
 * card data, and it was being dropped with the PAN.
 */
export function matchesSensitiveName(name: string): boolean {
  if (STRONG_SENSITIVE_PATTERNS.some(re => re.test(name))) return true
  if (CREDENTIAL_NAME_PATTERNS.some(re => re.test(name))) return true
  if (!CARD_NAME_PATTERNS.some(re => re.test(name))) return false
  return !SAFE_QUALIFIER_PATTERNS.some(re => re.test(name))
}

/**
 * `autocomplete` is a space-separated token list that may carry `section-*`,
 * `shipping`/`billing` and a `webauthn` suffix (e.g. `billing cc-number`), so
 * match any `cc-` token rather than the whole string. Covers `cc-number`,
 * `cc-csc`, `cc-exp*`, `cc-name` and the `cc-given-name` family in one rule.
 */
export function hasCardAutocomplete(value: string | null | undefined): boolean {
  if (!value) return false
  return value.trim().toLowerCase().split(/\s+/).some(token => token.startsWith('cc-'))
}

/**
 * Luhn check over 13–19 digits — the length window every major card network
 * fits in. Catches a PAN in a field whose name says nothing.
 *
 * Known false positives, accepted on purpose: a CNPJ (14 digits) or a 13-digit
 * millisecond timestamp passes Luhn roughly one time in ten. Losing an analytics
 * field beats shipping a card number, and a CNPJ is PII the pipeline is better
 * off without anyway.
 */
export function looksLikeCardNumber(value: string): boolean {
  if (!NUMERIC_ISH.test(value)) return false
  const digits = value.replace(/\D/g, '')
  if (digits.length < 13 || digits.length > 19) return false

  let sum = 0
  let double = false
  for (let i = digits.length - 1; i >= 0; i--) {
    let digit = digits.charCodeAt(i) - 48
    if (double) {
      digit *= 2
      if (digit > 9) digit -= 9
    }
    sum += digit
    double = !double
  }
  return sum % 10 === 0
}

/**
 * Tokens de `autocomplete` que declaram credencial — o equivalente ao `cc-*`
 * para cartão. Vale por si porque é intenção declarada pela página: um campo
 * `autocomplete="new-password"` é senha mesmo que o `type` esteja `text` (é o
 * que acontece com o botão "mostrar senha").
 */
const CREDENTIAL_AUTOCOMPLETE_TOKENS = new Set([
  'current-password',
  'new-password',
  'one-time-code',
])

export function hasCredentialAutocomplete(value: string | null | undefined): boolean {
  if (!value) return false
  return value.trim().toLowerCase().split(/\s+/).some(token => CREDENTIAL_AUTOCOMPLETE_TOKENS.has(token))
}

/** An `<input type="password">`, or a card/credential field by autocomplete. */
function isSensitiveInput(element: Element): boolean {
  if (!(element instanceof HTMLInputElement)) return false
  if (element.type === 'password') return true
  const autocomplete = element.getAttribute('autocomplete')
  return hasCardAutocomplete(autocomplete) || hasCredentialAutocomplete(autocomplete)
}

/**
 * Whether a submitted field must NOT be captured.
 *
 * `element` is what `form.elements.namedItem(name)` returned: a single control,
 * a `RadioNodeList` when several controls share the name, or `null` when the
 * FormData entry has no matching control. The name and value checks run
 * regardless, so a field with no reachable element is still covered.
 */
export function isSensitiveFormField(
  element: Element | RadioNodeList | null,
  name: string,
  value: string,
): boolean {
  if (element) {
    if (element instanceof RadioNodeList) {
      for (const item of element) {
        if (isSensitiveInput(item)) return true
      }
    } else if (isSensitiveInput(element)) {
      return true
    }
  }
  return matchesSensitiveName(name) || looksLikeCardNumber(value)
}
